import {
	Alert,
	Backdrop,
	CircularProgress,
	Grid,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles/makeStyles';
import { pageSizeByDefault, pageSizeLargeOptions } from 'constants/constants';
import { FilterGrid } from 'features/common/components/FilterGrid/FilterGrid';
import {
	convertFiltersToRecord,
	createSortHandler,
	ExportExcelOptions,
	exportToExcel,
	formatDateTimeToShowUser,
	getEnvelopedListPageTotal,
	getSortableFields,
} from 'features/common/helpers';
import {
	FilterModel,
	FilterType,
	IHeadCell,
	ISort,
	OperatorFilterEnum,
	SelectFilterModelSimple,
	ShowAlertState,
} from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import React, { useEffect, useState } from 'react';
import { ExportToExcelButton } from '../../../common/components/ExportToExcelButton';
import { PageNoResult } from '../../../common/components/PageNoResults';
import { activityLogActionList, activityLogModuleList, IActivityLogList } from '../../types';
import { getActivityLogList, getActivityLogListToExport } from '../../services';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	tableHeader: {
		backgroundColor: theme.palette.primary.main,
		color: 'white',
	},
	paginationHolder: {
		marginTop: theme.spacing(1),
		padding: 10,
		justifyContent: 'center',
		alignItems: 'center',
		display: 'flex',
	},
}));

const headerCells: IHeadCell[] = [
	{
		field: 'username',
		sortable: true,
		headerName: 'Usuario',
	},
	{
		field: 'fullName',
		sortable: true,
		headerName: 'Nombre completo de usuario',
	},
	{
		field: 'module',
		sortable: true,
		headerName: 'Módulo',
	},
	{
		field: 'action',
		sortable: true,
		headerName: 'Acción',
	},
	{
		field: 'remarks',
		sortable: true,
		headerName: 'Descripción',
	},
	{
		field: 'created',
		sortable: true,
		headerName: 'Fecha de Creación',
	},
];

export const ActivityLogList = (): JSX.Element => {
	const classes = useStyles();
	const [loading, setLoading] = useState<boolean>(false);
	const [currentSize, setCurrentSize] = useState<number>(pageSizeByDefault);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [activityLogList, setActivityLogList] = useState<IActivityLogList[] | null>(null);
	const [sortFields, setSortFields] = useState<ISort[]>([{ field: 'created', order: 'desc' }]);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);

	const filters = [
		{
			label: 'Usuario',
			type: FilterType.STRING,
			key: 'username',
			operator: OperatorFilterEnum.CONTAINS,
		},
		{
			label: 'Nombre completo de usuario',
			type: FilterType.STRING,
			key: 'username',
			operator: OperatorFilterEnum.CONTAINS,
		},
		new SelectFilterModelSimple('Módulo', 'module', activityLogModuleList),
		new SelectFilterModelSimple('Acción', 'action', activityLogActionList),

		{ label: 'Fecha de Creada mayor o igual a', type: FilterType.DATE, key: 'from' },
		{ label: 'Fecha de Creada menor o igual a', type: FilterType.DATE, key: 'to' },
	];

	const loadActivityLog = async () => {
		try {
			setLoading(true);
			const envelopedActivityLogList = (
				await getActivityLogList(currentPage - 1, currentSize, sortFields, convertFiltersToRecord(filterApplied))
			).data;
			setPageTotal(getEnvelopedListPageTotal(envelopedActivityLogList));
			setActivityLogList(envelopedActivityLogList.data);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado de activity logs'),
			});
			setLoading(false);
		}
	};

	useEffect(() => {
		loadActivityLog();
	}, [filterApplied, sortFields, currentPage, currentSize]);

	const handleApplyFilters = (filters: FilterModel[]) => setFilterApplied([...filters]);

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const sortableFields = getSortableFields(headerCells);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const transformValue = (key: string, value: any, row: IActivityLogList) => {
		switch (key) {
			case 'created':
				return formatDateTimeToShowUser(value);
			default:
				return value;
		}
	};

	const handleExportToExcel = async () => {
		try {
			setLoading(true);
			const envelopedActivityLogList = (
				await getActivityLogListToExport(sortFields, convertFiltersToRecord(filterApplied))
			).data;
			const activityLogList = envelopedActivityLogList.data;
			const optionsToExport = {
				title: 'Listado de activity logs',
				headers: headerCells,
				widthColumns: [20, 20, 20, 20, 20, 20],
				filename: 'Listado de activity logs',
			} as ExportExcelOptions;
			exportToExcel(activityLogList, optionsToExport, transformValue);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al exportar el listado de activity logs'),
			});
			setLoading(false);
		}
	};

	const colSpan = headerCells.length;

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid container gap={1}>
				<Grid container spacing={2}>
					<Grid item xs={11}>
						<FilterGrid filters={filters} handleAppliedFilters={handleApplyFilters} preAppliedFilters={filterApplied} />
					</Grid>
					<Grid item xs={1}>
						<ExportToExcelButton disabled={activityLogList?.length == 0} handleExportToExcel={handleExportToExcel} />
					</Grid>
				</Grid>
				<TableContainer component="main" sx={{ maxWidth: 'xl' }}>
					<div>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									{headerCells.map((cell) => {
										return cell.sortable ? (
											<TableCell
												key={cell.field}
												align="center"
												className={classes.tableHeader}
												sortDirection={
													sortFields.find((x) => x.field === cell.field)
														? sortFields.find((x) => x.field === cell.field)?.order
														: false
												}
											>
												<TableSortLabel
													active={cell.sortable && sortFields.find((x) => x.field === cell.field) !== undefined}
													sx={{
														color: 'white !important',
														'&:hover': {
															color: 'white !important',
														},
													}}
													direction={
														sortFields.find((x) => x.field === cell.field)
															? sortFields.find((x) => x.field === cell.field)?.order
															: 'asc'
													}
													onClick={createSortHandler(cell.field, sortFields, sortableFields, setSortFields)}
												>
													{cell.headerName}
												</TableSortLabel>
											</TableCell>
										) : (
											<TableCell className={classes.tableHeader} key={cell.field} align="center">
												{cell.headerName}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{!loading && activityLogList?.length === 0 ? (
									<TableRow>
										<TableCell colSpan={colSpan}>
											<PageNoResult />
										</TableCell>
									</TableRow>
								) : (
									activityLogList?.map((activityLog) => (
										<TableRow
											key={`${activityLog.username}-${activityLog.fullName}-${activityLog.module}-${activityLog.action}-${activityLog.created}`}
											hover
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell align="right" component="p">
												{activityLog.username}
											</TableCell>
											<TableCell align="right" component="p">
												{activityLog.fullName}
											</TableCell>
											<TableCell align="right" component="p">
												{activityLog.module}
											</TableCell>
											<TableCell align="right" component="p">
												{activityLog.action}
											</TableCell>
											<TableCell align="right" component="p">
												{activityLog.remarks}
											</TableCell>
											<TableCell align="right" component="p">
												{formatDateTimeToShowUser(activityLog.created)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</TableContainer>
				<Grid item xs={12}>
					<Grid container justifyContent="center" flexDirection="row" padding={2}>
						<Grid item xs={12} md={1} textAlign="center">
							<Select value={currentSize} onChange={handleRowsPerPageChange}>
								{pageSizeLargeOptions.map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12} md={2} textAlign="center" marginTop={1}>
							<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
